import { ReactNode, useRef } from 'react';
import { SessionData, useSessionStore } from '../../hooks/stores/sessionStore';
import { UIData, useUIStore } from '../../hooks/stores/uiStore';

import { DropdownMenu } from '../common/DropdownMenu/DropdownMenu';
import { KeyboardKey } from '../common/KeyboardKey';
import Launcher1Theme from '../../info/launcherTemplates/Launcher1Theme.json';
import configAPI from "../../configAPI.json";
import { launcherElementInfo } from '../../info/launcherElementInfo';
import { toolbarInfo } from '../../info/toolbarInfo';
import { useLauncherElementsStore } from '../../hooks/stores/launcherElementsStore';
import { useScene } from '../../context/sceneContext';

export interface MenuItem {
  label?: string | ReactNode;
  rightSlot?: string | ReactNode;
  onClick?: () => void;
  items?: MenuItem[];
  separator?: boolean;
  closeOnClick?: boolean;
  isDisabled?: boolean;
}

export const TopbarMenu = () => {
  const [sessionData] = useSessionStore<SessionData>();
  const [UIData, setUIData] = useUIStore<UIData>();
  const scene = useScene();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [launcherElementsData] = useLauncherElementsStore();
  // TODO: add back licensePopup functionality
  // const isLicensePopupAdded = Object.keys(launcherElementsData).some(
  //   (id) => id === 'appLicenseKeyPopup'
  // );

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          const fileContent = e.target.result as string;
          scene.importConfig(fileContent);
        }
      };
      reader.readAsText(file);
    }
  };

  const newTheme = () => {
    scene.importConfig(
      JSON.stringify({
        formatVersion: '2.0',
        windowWidth: 800,
        windowHeight: 600,
        isWindowResizable: false,
        fonts: [
          {
            "fontFamily": "Inter",
            "src": "https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900"
          }
        ],
        class: {
          boxSizing: 'border-box',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'white',
          fontFamily: 'Inter',
          overflow: 'hidden',
          color: '#eeeeee',
          fontWeight: 300,
          fontSize: '14px',
          borderRadius: '10px',

          ['*']: {
            transition: 'all 0.3s ease',
          },
          ['::-webkit-scrollbar']: { width: '6px', height: '6px' },
          ['::-webkit-scrollbar-track']: {
            backgroundColor: '#00000080',
            borderRadius: '3px',
          },
          ['::-webkit-scrollbar-corner']: { background: 'transparent' },
          ['::-webkit-scrollbar-thumb']: {
            borderRadius: '3px',
            backgroundColor: '#ffffff80',
          },
        },
        children: [],
      })
    );

    scene.deselectAllElements();
  };

  const data: MenuItem[] = [
    {
      label: 'File',
      items: [
        ...(sessionData.isAuthorized ? [{
          label: 'Save & Publish',
          rightSlot: <KeyboardKey>s</KeyboardKey>,
          onClick: () => {
            scene.saveThemeVersion();
          },
        }] : []),
        { label: 'New', onClick: () => newTheme() },
        {
          label: 'Open',
          items: [
            {
              label: 'Launcher 1',
              onClick: () => {
                scene.importConfig(JSON.stringify(Launcher1Theme), true);
                setUIData({
                  currentLauncherState: [null, null],
                  selectedElementIDs: [],
                });
              },
            },
            // {
            //   label: 'New UI Theme',
            //   onClick: () => {
            //     scene.importConfig(JSON.stringify(NewUITheme), true);
            //     setUIData({
            //       currentLauncherState: [null, null],
            //       selectedElementIDs: [],
            //     });
            //   },
            // },
            // {
            //   label: 'DEMO Theme',
            //   onClick: () => {
            //     scene.importConfig(JSON.stringify(DemoTheme), true);
            //     setUIData({
            //       currentLauncherState: [null, null],
            //       selectedElementIDs: [],
            //     });
            //   },
            // },
          ],
        },
        ...(sessionData.isAuthorized ? [{
          label: 'Version History',
          rightSlot: <i className="icon-popup" />,
          onClick: () => scene.openModal('versionHistoryModal'),
        }] : []),
        {
          separator: true,
        },
        {
          label: 'Import',
          items: [
            {
              label: 'Paste JSON from clipboard',
              onClick: () => {
                const input = prompt('Please paste the config JSON');
                if (input !== null && input !== '') {
                  scene.importConfig(input);
                }
              },
            },
            {
              label: 'Upload JSON file',
              onClick: () => {
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              },
            },
          ],
        },
        ...(sessionData.isAuthorized || true ? [{
          label: 'Export',
          items: [
            {
              label: 'Copy JSON to clipboard',
              onClick: () => {
                const config = scene.exportConfig();
                navigator.clipboard.writeText(config);
              },
            },
            {
              label: 'Download JSON file',
              onClick: () => {
                const config = scene.exportConfig();
                const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(config);
                const downloadAnchorNode = document.createElement('a');
                downloadAnchorNode.setAttribute('href', dataStr);
                downloadAnchorNode.setAttribute('download', 'launcherConfig.json');
                document.body.appendChild(downloadAnchorNode); // required for firefox
                downloadAnchorNode.click();
                downloadAnchorNode.remove();
              },
            },
          ],
        }] : []),
        {
          separator: true,
        },
        {
          label: 'Open Themes Page',
          rightSlot: <i className="icon-external" />,
          onClick: () => window.open(configAPI.endpoints.themesPage, '_blank'),
        },
      ],
    },
    {
      label: 'Edit',
      isDisabled: UIData.editorMode === 'simulation',
      items: [
        {
          label: 'Undo',
          rightSlot: <KeyboardKey>z</KeyboardKey>,
          onClick: () => scene.historyUndo(),
        },
        {
          label: 'Redo',
          rightSlot: <><KeyboardKey>shift</KeyboardKey>+<KeyboardKey>z</KeyboardKey>/<KeyboardKey>y</KeyboardKey></>,
          onClick: () => scene.historyRedo(),
        },
        {
          label: 'Duplicate Selected Elements',
          rightSlot: <><KeyboardKey>d</KeyboardKey></>,
          onClick: () => scene.duplicateSelectedElements(),
        },
        {
          label: 'Copy Selected Elements',
          rightSlot: <><KeyboardKey>c</KeyboardKey></>,
          onClick: () => scene.copySelectedElements(),
        },
        {
          label: 'Paste Selected Elements',
          rightSlot: <><KeyboardKey>v</KeyboardKey></>,
          onClick: () => scene.pasteElements(),
        },
        {
          label: 'Delete Selected Elements',
          rightSlot: <><KeyboardKey>del</KeyboardKey>/<KeyboardKey>⌫</KeyboardKey></>,
          onClick: () => scene.deleteSelectedElements(),
        },
      ],
    },
    {
      label: 'View',
      isDisabled: UIData.editorMode === 'simulation',
      items: [
        {
          label: <>{UIData.showAbsoluteGuides ? '✓ ' : ''}Element Position Guides</>,
          onClick: () => setUIData({ 
            showAbsoluteGuides: !UIData.showAbsoluteGuides,
            showRelativeGuides: !UIData.showRelativeGuides 
          }),
          rightSlot: <KeyboardKey>g</KeyboardKey>
        },
        {
          separator: true
        },
        {
          label: <>{UIData.openModalIDs.includes('advancedLauncherStatesPanel') ? '✓ ' : ''}Advanced States Panel</>,
          onClick: () => setUIData({
            openModalIDs: UIData.openModalIDs.includes('advancedLauncherStatesPanel')
              ? UIData.openModalIDs.filter(id => id !== 'advancedLauncherStatesPanel')
              : [...UIData.openModalIDs, 'advancedLauncherStatesPanel']
          }),
          rightSlot: <KeyboardKey>1</KeyboardKey>
        },
        {
          label: <>{UIData.openModalIDs.includes('advancedElementInfoPanel') ? '✓ ' : ''}Advanced Element Info Panel</>,
          onClick: () => setUIData({
            openModalIDs: UIData.openModalIDs.includes('advancedElementInfoPanel')
              ? UIData.openModalIDs.filter(id => id !== 'advancedElementInfoPanel')
              : [...UIData.openModalIDs, 'advancedElementInfoPanel']
          }),
          rightSlot: <KeyboardKey>2</KeyboardKey>
        },
        {
          separator: true
        },
        {
          label: <>{UIData.useDirectVisibility ? '✓ ' : ''}Use Direct Visibility</>,
          onClick: () => setUIData({ useDirectVisibility: !UIData.useDirectVisibility }),
        },
      ]
    },
    {
      label: 'Insert',
      isDisabled: UIData.editorMode === 'simulation',
      items: toolbarInfo.map(({ elementType }) => ({
        label: launcherElementInfo[elementType].name,
        onClick: () => scene.addElement(elementType),
        // TODO: add back licensePopup functionality
        // isDisabled: elementType === 'licensePopup' && isLicensePopupAdded,
      })) as MenuItem[],
    },
    {
      label: 'Help',
      items: [
        {
          label: 'Support',
          rightSlot: <i className="icon-external" />,
          onClick: () => {
            window.open('https://patchkit.net/support.html');
          },
        },
        {
          label: 'Send Feedback',
          rightSlot: <i className="icon-popup" />,
          onClick: () => {
            scene.openModal('feedbackModal');
          },
        },
      ],
    },
  ];

  return <>
    <input
      type="file"
      accept=".json"
      style={{ display: 'none' }}
      ref={fileInputRef}
      onChange={(e) => handleFileChange(e)}
    />

    <DropdownMenu data={data} />
  </>;
};





